import { Base } from '@studiometa/js-toolkit';

/**
 * @typedef {import('@studiometa/js-toolkit').BaseProps & {
 *  $refs: {
 *    wrapper: HTMLDivElement,
 *    field: HTMLInputElement,
 *    error: HTMLParagraphElement,
 *  }
 * }} QuantityProps
 * @typedef {{
 *  product_prices: string,
 *  product_cover_thumbnails: string,
 *  product_customization: string,
 *  product_details: string,
 *  product_variants: string,
 *  product_discounts: string,
 *  product_add_to_cart: string,
 *  product_additional_info: string,
 *  product_images_modal: string,
 *  product_flags: string,
 *  product_url: string,
 *  product_minimal_quantity: string,
 *  product_has_combinations: boolean,
 *  id_product_attribute: number,
 *  id_customization: number,
 *  product_title: string,
 *  is_quick_view: boolean,
 * }} Sections
 */

/**
 * Quantity class.
 * @augments Base<QuantityProps>
 */
export default class Quantity extends Base {
  static config = {
    name: 'Quantity',
    refs: ['wrapper', 'field', 'error'],
  };

  /**
   * @type {number|null}
   */
  _min = null;

  /**
   * @type {number|null}
   */
  _max = null;

  /**
   * Get min attribute.
   * @returns {number|false}
   */
  get min() {
    if (this._min !== null) {
      return this._min;
    }

    const { min } = this.$refs.field;

    if (!min) {
      this._min = false;
      return this._min;
    }

    this._min = Number.parseInt(min, 10);

    return this._min;
  }

  /**
   * Get max attribute.
   * @returns {number|false}
   */
  get max() {
    if (this._max !== null) {
      return this._max;
    }

    const { max } = this.$refs.field;

    if (!max) {
      this._max = false;
      return this._max;
    }

    this._max = Number.parseInt(max, 10);

    return this._max;
  }

  /**
   * Get the quantity.
   * @returns {number|null}
   */
  get quantity() {
    const { value } = this.$refs.field;

    if (!value) {
      return null;
    }

    return Number.parseInt(value, 10);
  }

  /**
   * @returns {void}
   */
  mounted() {
    window.prestashop.on('updatedProduct', this.handleProductUpdated.bind(this));
  }

  /**
   * On field input event.
   * @returns {void}
   */
  onFieldInput() {
    this.resetError();

    const { field } = this.$refs;

    if (!this.quantity && this.quantity !== 0) {
      return;
    }

    if (this.min !== false && this.quantity < this.min) {
      field.value = this.min;
      this.displayError(`Vous devez acheter au minimum ${this.min} produit(s)`);
      return;
    }

    if (this.max !== false && this.quantity > this.max) {
      field.value = this.max;
      this.displayError(`Stock insuffisant, il ne reste que ${this.max} produit(s)`);
    }
  }

  /**
   * On field change event.
   * @returns {void}
   */
  onFieldChange() {
    this._quantity = null;

    if (this.quantity) {
      return;
    }

    this.$refs.field.value = this.min;
  }

  /**
   * Handle productUpdated event from Prestashop.
   * @param {Sections} sections
   * @returns {void}
   */
  handleProductUpdated(sections) {
    this.resetError();
    const newDom = new DOMParser().parseFromString(sections.product_add_to_cart, 'text/html');
    const newWrapper = newDom.querySelector('[data-ref="wrapper"]');

    if (!newWrapper) {
      return;
    }

    this.$refs.wrapper.outerHTML = newWrapper.outerHTML;
    this.$update();
    this._min = null;
    this._max = null;
  }

  /**
   * Display error message.
   * @param {string} message
   * @returns {void}
   */
  displayError(message) {
    const { error } = this.$refs;
    error.textContent = message;
    error.style.display = null;
  }

  /**
   * Reset error message.
   * @returns {void}
   */
  resetError() {
    const { error } = this.$refs;
    error.textContent = null;
    error.style.display = 'none';
  }
}
